import { useSite } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { PageIntro } from "@/components/site/PageIntro";
import { AgencySection, HowItWorks } from "@/components/home/Sections";
import { FaqList, faqJsonLd } from "@/components/site/FaqList";
import { Loading, Empty } from "@/components/site/Primitives";
import { AboutSection, ParentLink } from "@/components/home/About";
import { orgLd, breadcrumbLd } from "@/lib/jsonld";

export function About() {
  const { site } = useSite();
  useSeo("about", "/about", {}, site ? [orgLd(site), breadcrumbLd(site, [["Home", "/"], ["About", "/about"]])] : null);
  if (!site) return <div className="eb-container py-24"><Loading rows={2} /></div>;
  return (
    <div data-testid="about-page">
      <PageIntro eyebrow="About" title={site.parent?.about_title} body={site.brand?.description} />
      <AboutSection site={site} />
      <div className="eb-container"><HowItWorks meta={{ title: "One coordinated workflow" }} site={site} /></div>
      <div className="eb-container pb-8"><ParentLink parent={site.parent} /></div>
    </div>
  );
}

export function Agency() {
  const { site } = useSite();
  useSeo("agency", "/agency");
  if (!site) return <div className="eb-container py-24"><Loading rows={2} /></div>;
  return (
    <div data-testid="agency-page">
      <PageIntro eyebrow="Agencies" title={site.agency?.title} body={site.agency?.body} />
      <AgencySection meta={{ title: "White-label" }} site={site} />
      <HowItWorks meta={{ title: "How we work with your team" }} site={site} />
    </div>
  );
}

export function Faq() {
  const { faqs, loading } = useSite();
  useSeo("faq", "/faq", {}, [faqJsonLd(faqs)]);
  return (
    <div data-testid="faq-page">
      <PageIntro eyebrow="FAQ" title="Questions, answered." />
      <div className="eb-container py-16 max-w-4xl">
        {loading ? <Loading rows={1} /> : faqs.length ? <FaqList faqs={faqs} /> : <Empty title="No FAQs published yet." />}
      </div>
    </div>
  );
}

export function Privacy() {
  const { site } = useSite();
  useSeo("privacy", "/privacy");
  return (
    <div data-testid="privacy-page">
      <PageIntro eyebrow="Legal" title="Privacy & data." />
      <div className="eb-container py-16 max-w-3xl space-y-6 text-zinc-300 leading-relaxed">
        {(site?.privacy?.body || "").split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
      </div>
    </div>
  );
}

export function NotFound() {
  return (
    <div className="eb-container py-32" data-testid="not-found-page">
      <p className="eb-label">404</p>
      <h1 className="mt-4 text-5xl font-semibold tracking-[-0.04em]">Cut from the <span className="eb-accent">edit.</span></h1>
      <p className="mt-4 text-zinc-400">This page doesn't exist.</p>
      <a href="/" className="eb-btn-primary mt-8">Back home</a>
    </div>
  );
}
